import { Component, OnInit, Input, ViewChild, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { Note } from 'app/models/dto/note.model';
import { Store, Select } from '@ngxs/store';
import { NoteState } from 'app/store/notes/note.state';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil, filter } from 'rxjs/operators';
import { SubscribeToBorrowerNotes, AddNote, DeleteNote, UpdateNote, ClearNoteStore } from 'app/store/notes/note.actions';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { NotesComponent } from '../notes/notes.component';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { DealNoteState } from 'app/store/dealNotes/dealNote.state';
import { DealNote } from 'app/models/dto/dealNote.model';
import { AddDealNote, DeleteDealNote, GetDealNotes, UpdateDealNote } from 'app/store/dealNotes/dealNote.action';

@Component({
  selector: 'app-notes-smart',
  templateUrl: './notes-smart.component.html',
  styleUrls: ['./notes-smart.component.scss'],
  standalone: false
})
export class NotesSmartComponent implements OnInit, OnDestroy {

  lenderUserId: number;
  destroyed$ = new Subject<void>();
  destroying: boolean = false;
  draft: { content: string } = { content: '' };

	@Select(NoteState.notes) notes$: Observable<Note[]>;
  @Select(DealNoteState.dealNotes) dealNotes$: Observable<DealNote[]>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Input() addNoteOpen: boolean = false;
  @Input() borrowerId: number;
  @Input() borrowerName: string;
  @Input() includeBorrowerName: boolean = false;
  @Input() dealId: number;
  @Input() expanded: boolean;
  @Input() hideHeader: boolean = false;
  @Input() inContainer: boolean = false;
  @Input() useDealNotes: boolean = false;

  @Output() latestNoteChanged = new EventEmitter<Note[]>();

  @ViewChild('notesComponent', { static: true })
  notesComponent: NotesComponent;
  currentNotes: Note[] = [];

	constructor(
		private store: Store,
    private eventBusService: EventBusService,
    private changeDetectorRef: ChangeDetectorRef
	) {}

	ngOnInit(): void {
    this.destroyed$.subscribe(() => {
      this.destroying = true;
    });

    this.loadDraft();
    if (this.useDealNotes) {
      this.store.dispatch(new GetDealNotes(this.dealId));
    } else {
      this.store.dispatch(new SubscribeToBorrowerNotes(this.borrowerId));
      this.store.select(NoteState.notes).pipe(
        takeUntil(this.destroyed$),
        filter(() => !this.destroying)
      ).subscribe((notes) => {
          this.currentNotes = notes;
          this.emitLatestNoteChange();
        });
    }
		this.lenderUser$.pipe(take(1)).subscribe((user) => {
			this.lenderUserId = user?.id;
    });
    this.eventBusService.subscribe(this.eventBusService.types.OpenAddNoteEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.notesComponent.openAddNote());
	}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.store.dispatch(new ClearNoteStore(this.borrowerId));
  }

	handleAddNote(newNote): void {
		this.lenderUser$.pipe(take(1)).subscribe((user) => {
			this.fillAndSaveNewNote(newNote, user);
		});
    this.clearDraft();
    this.addNoteOpen = false;
	}

	handleDeleteNote(noteId: number): void {
		this.deleteNote(noteId);
    this.addNoteOpen = false;
	}


	handleUpdateNote(event: {id: number, content: string}): void {
		this.updateNote(event.id, event.content);
    this.addNoteOpen = false;
    this.changeDetectorRef.detectChanges();
  }

  handleEnterEditNote(event){
    this.addNoteOpen = false;
    this.changeDetectorRef.detectChanges();
  }

  emitLatestNoteChange(): void {
    this.latestNoteChanged.emit(this.currentNotes);
  }

  loadDraft(): void {
    const json = localStorage.getItem(this.draftKey()) || null;
    const draft = json ? JSON.parse(json) : { content: '' };
    this.draft = draft;
  }

  saveDraft(draft) {
    const content = draft.content || '';
    if (content.trim() === '') {
      this.clearDraft();
      return;
    }
    localStorage.setItem(this.draftKey(), JSON.stringify(draft));
    this.draft = draft;
  }

  clearDraft() {
    localStorage.removeItem(this.draftKey());
    this.draft = {content: ''};
  }

  draftKey() {
    return `borrower-${this.borrowerId}-noteDraft`;
  }

  toggleAddNote(): void {
    this.eventBusService.publish(new this.eventBusService.types.OpenCollapsibleContainerEvent('notes'));
    this.addNoteOpen = !this.addNoteOpen;
  }

  private fillAndSaveNewNote(newNote, user: LenderUser) {
    newNote.lenderUserName = user.fullName;
    newNote.lenderUserId = user.id;
    newNote.created = new Date();
    if (this.useDealNotes) {
      newNote.dealId = this.dealId;
      this.store.dispatch(new AddDealNote(newNote));
    } else {
      newNote.borrowerId = this.borrowerId;
      newNote.noteType = 'note';
      this.store.dispatch(new AddNote(newNote));
    }
  }

  private deleteNote(id: number) {
    if (this.useDealNotes) {
      this.store.dispatch(new DeleteDealNote(id));
    } else {
      this.store.dispatch(new DeleteNote(id));
    }
  }

  private updateNote(id: number, content: string) {
    if (this.useDealNotes) {
      this.store.dispatch(new UpdateDealNote(id, content));
    } else {
      this.store.dispatch(new UpdateNote(id, content));
    }
  }
}
